import { useState } from "react";
import { Link } from "react-router-dom";
import { FaTrash, FaEdit, FaLock, FaCrown, FaArrowRight, FaBookOpen } from "react-icons/fa";
// eslint-disable-next-line no-unused-vars
import { deleteCourse } from "../../apis/CourseApis";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useTheme } from "../Theme/ThemeContext";

export function CourseCard({ course, user = {}, onCourseDeleted }) {
  const { role } = user;
  const { successToast, errorToast, warningToast } = useToastHelpers();
  const { currentTheme } = useTheme();
  const [isDeleting, setIsDeleting] = useState(false);

  const isStatic = Boolean(course?.isStatic);
  const isLocked = Boolean(course?.isLocked);
  const canAdminManage = role === "admin" && !isStatic;

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      setIsDeleting(true);
      try {
        // await deleteCourse(course.id, user.token);
        successToast("Course deleted successfully!");
        onCourseDeleted?.(course.id);
      } catch (err) {
        console.error("Error deleting course:", err);
        errorToast("Failed to delete course");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleLockedClick = () => {
    warningToast("Course locked", "This course is currently locked.");
  };

  return (
    <div className={`relative ca-card-hover overflow-hidden ${isLocked ? 'opacity-80' : ''}`}>
      {/* Course Header with gradient */}
      <div className={`h-1 ${isLocked ? 'bg-gradient-to-r from-neutral-400 to-neutral-500' : `bg-gradient-to-r ${currentTheme.colors.gradient}`}`}></div>
      
      <div className="p-6">
        {/* Badges */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            {isLocked && (
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold bg-neutral-100/50 dark:bg-white/5 text-neutral-600 dark:text-neutral-400">
                <FaLock className="w-3 h-3" /> Locked
              </span>
            )}
          </div>
          {course.isPremium && (
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm">
              <FaCrown className="w-3 h-3" /> Premium
            </span>
          )}
        </div>

        {/* Course Icon */}
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isLocked ? 'bg-neutral-100/50 dark:bg-white/5' : `bg-gradient-to-br ${currentTheme.colors.gradientSubtle}`}`}>
          <FaBookOpen className={`w-6 h-6 ${isLocked ? 'text-neutral-400 dark:text-neutral-500' : 'text-theme-primary'}`} />
        </div>

        {/* Course Content */}
        <div className="mb-5">
          <h3 className="text-lg font-bold text-neutral-800 dark:text-white mb-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors line-clamp-2">
            {course.title}
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed line-clamp-2">
            {course.description}
          </p>
        </div>

        {/* Buttons Section */}
        <div className="space-y-3">
          {/* View Course Button */}
          {isLocked || isStatic ? (
            <button
              type="button"
              onClick={handleLockedClick}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium text-neutral-400 dark:text-neutral-500 bg-neutral-100/50 dark:bg-white/5 cursor-not-allowed"
            >
              <FaLock className="w-3.5 h-3.5" /> Course Locked
            </button>
          ) : (
            <Link 
              to={`/courses/${course.id}`} 
              className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 shadow-md ${currentTheme.colors.glow} hover:shadow-lg transition-all duration-300 group/btn`}
            >
              Start Learning
              <FaArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          )}

          {/* Admin Buttons */}
          {canAdminManage && (
            <div className="flex gap-2 pt-2 border-t border-neutral-200/30 dark:border-white/10">
              <Link
                to={`/courses/edit/${course.id}`}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium text-neutral-600 dark:text-neutral-300 bg-neutral-100/50 dark:bg-white/5 hover:bg-neutral-200/50 dark:hover:bg-white/10 transition-colors"
              >
                <FaEdit className="w-3.5 h-3.5" /> Edit
              </Link>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 bg-red-50/50 dark:bg-red-900/20 hover:bg-red-100/50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
              >
                <FaTrash className="w-3.5 h-3.5" /> {isDeleting ? "..." : "Delete"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
